import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { Children, FC, PropsWithChildren } from "react";
import { createSlots } from "../../utils/create-slots";
import { Icon } from "../Icon";

type Width = "narrow" | "fullwidth" | "medium";
type Position = "top" | "bottom" | "center";

const dialogStyles = cva(
  "bg-white fixed w-full  my-6 mx-auto z-[90] left-1/2 overflow-y-scroll  -translate-x-1/2 rounded p-4 max-h-screen",
  {
    variants: {
      position: {
        top: "top-0 mt-0",
        bottom: "bottom-0 mb-0",
        center:
          "bottom-0 md:bottom-1/2 b-0 my-auto mb-0 md:mt-5 md:mb-5 md:translate-y-1/2",
      },
      width: {
        narrow: "md:max-w-[560px]",
        fullwidth: "md:max-w-[100vw]",
        medium: "md:max-w-[80vw]",
      },
    },
    defaultVariants: {
      width: "medium",
      position: "center",
    },
  }
);

const Dialog: FC<
  PropsWithChildren<
    DialogPrimitive.DialogProps & {
      buttonClass?: string;
      position?: Position;
      className?: string;
      width?: Width;
      showDefaultDialogClose?: boolean;
      triggerLabel?: string;
      onPointerDownOutside?: (arg0: any) => any;
      onInteractOutside?: (arg0: any) => any;
      onEscapeKeyDown?: (arg0: any) => any;
    }
  >
> = ({
  children,
  buttonClass = "",
  position = "center",
  className = "",
  width = "medium",
  showDefaultDialogClose = true,
  onPointerDownOutside = () => {},
  onInteractOutside = () => {},
  onEscapeKeyDown = () => {},
  triggerLabel,
  ...rootProps
}) => {
  const [title, description, trigger, content] = createSlots(
    ["title", "description", "trigger", "content"],
    children
  );

  return (
    <DialogPrimitive.Root {...rootProps}>
      {Children.toArray(trigger).map((el, index) => (
        <DialogPrimitive.Trigger
          className={buttonClass}
          key={`trigger-${index}`}
          {...(triggerLabel ? { "aria-label": triggerLabel } : {})}
        >
          {el}
        </DialogPrimitive.Trigger>
      ))}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bg-gray-900 overflow-y-auto overflow-x-hidden inset-0 fixed md:h-full opacity-80 z-[90]" />
        <DialogPrimitive.Content
          onInteractOutside={onInteractOutside}
          onPointerDownOutside={onPointerDownOutside}
          onEscapeKeyDown={onEscapeKeyDown}
          className={`${dialogStyles({
            width,
            position,
          })} ${className}`}
        >
          <DialogPrimitive.Title>{title}</DialogPrimitive.Title>
          {description && (
            <DialogPrimitive.Description>
              {description}
            </DialogPrimitive.Description>
          )}
          {content}
          {showDefaultDialogClose && (
            <DialogPrimitive.Close>
              <Icon
                type="close"
                purposeLabel="Schliessen"
                className="w-6 h-6 top-5 right-5 text-primary absolute hover:text-blue-light"
              />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default Object.assign(Dialog, {
  Title: DialogPrimitive.Title,
  Content: DialogPrimitive.Content,
});
